import { getDoc, getDocs, query, where } from 'firebase/firestore';
import { cyclesCol, policiesCol, optionsConfigDoc, schoolDoc } from './firestore';
import { getSchoolCalendar } from './functions';
import type { Cycle, Policy } from '../types';

export type CalModule = 'pm' | 'policies' | 'options' | 'contract' | 'school';

export interface CalItem {
  id: string;
  date: Date;
  module: CalModule;
  label: string;
  /** Deadlines link to their source page. */
  href?: string;
  /** External calendar events carry their full detail for a popover. */
  detail?: { start: Date; end: Date | null; allDay: boolean; location?: string };
}

export const CAL_MODULE_META: Record<CalModule, { name: string; dot: string; chip: string }> = {
  pm:       { name: 'Performance Mgmt', dot: 'bg-blue-500',   chip: 'bg-blue-100 text-blue-800' },
  policies: { name: 'Policies',     dot: 'bg-rose-500',   chip: 'bg-rose-100 text-rose-800' },
  options:  { name: 'KS4 Options',  dot: 'bg-indigo-500', chip: 'bg-indigo-100 text-indigo-800' },
  contract: { name: 'Contract',     dot: 'bg-amber-500',  chip: 'bg-amber-100 text-amber-800' },
  school:   { name: 'School calendar', dot: 'bg-teal-500', chip: 'bg-teal-100 text-teal-800' },
};

const tsToDate = (t: { toDate?: () => Date } | undefined | null): Date | null => {
  try { return t?.toDate ? t.toDate() : null; } catch { return null; }
};

/**
 * Every dated thing a school cares about, in one list: module deadlines (PM
 * cycles, policy reviews, KS4 options close, contract end) plus events from the
 * subscribed external calendar. Each source is independent — a denied or empty
 * one never breaks the rest.
 */
export async function fetchCalendarItems(schoolId: string, isLeader: boolean): Promise<CalItem[]> {
  const out: CalItem[] = [];
  const [cycles, policies, optCfg, school, schoolEvents] = await Promise.all([
    getDocs(query(cyclesCol, where('schoolId', '==', schoolId))).then((s) => s.docs).catch(() => []),
    getDocs(query(policiesCol, where('schoolId', '==', schoolId))).then((s) => s.docs).catch(() => []),
    getDoc(optionsConfigDoc(schoolId)).then((s) => (s.exists() ? s.data() : null)).catch(() => null),
    isLeader ? getDoc(schoolDoc(schoolId)).then((s) => (s.exists() ? s.data() : null)).catch(() => null) : Promise.resolve(null),
    getSchoolCalendar({}).then((r) => r.data.events).catch(() => []),
  ]);

  const pmHref = isLeader ? '/cycles' : '/todo';
  const optHref = isLeader ? '/options/manage' : '/options';

  cycles.forEach((d) => {
    const c = d.data() as Cycle;
    const sd = tsToDate(c.staffDeadline); if (sd) out.push({ id: `${d.id}-staff`, date: sd, module: 'pm', label: `${c.name} — staff deadline`, href: pmHref });
    const lm = tsToDate(c.lineManagerDeadline); if (lm) out.push({ id: `${d.id}-lm`, date: lm, module: 'pm', label: `${c.name} — line manager deadline`, href: pmHref });
    const my = tsToDate(c.midYearDate); if (my) out.push({ id: `${d.id}-mid`, date: my, module: 'pm', label: `${c.name} — mid-year check-in`, href: pmHref });
  });
  policies.forEach((d) => {
    const p = d.data() as Policy;
    const nr = tsToDate(p.nextReviewDate); if (nr) out.push({ id: `pol-${d.id}`, date: nr, module: 'policies', label: `${p.title} — review due`, href: '/policies' });
  });
  if (optCfg?.['deadline']) {
    const od = new Date(`${optCfg['deadline']}T00:00:00`);
    if (!Number.isNaN(od.getTime())) out.push({ id: 'options-deadline', date: od, module: 'options', label: 'KS4 options close', href: optHref });
  }
  const ce = tsToDate(school?.['contractEndsAt'] as { toDate?: () => Date } | undefined);
  if (ce) out.push({ id: 'contract-end', date: ce, module: 'contract', label: 'Contract ends', href: '/school/settings' });

  schoolEvents.forEach((e, i) => {
    const d = new Date(e.start);
    if (Number.isNaN(d.getTime())) return;
    const end = e.end ? new Date(e.end) : null;
    out.push({
      id: `sch-${e.uid ?? i}`,
      date: d,
      module: 'school',
      label: e.summary,
      detail: { start: d, end: end && !Number.isNaN(end.getTime()) ? end : null, allDay: e.allDay, location: e.location },
    });
  });

  return out;
}
